// Signature element: a live feed that lights up whenever a WebMCP tool fires
// (agent) or a human acts (user). This makes the "agent-native" thesis visible.
import { actions } from "../store";
import type { ActivityEntry } from "../types";

function timeAgo(at: number): string {
  const s = Math.round((Date.now() - at) / 1000);
  if (s < 5) return "now";
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  return `${Math.round(m / 60)}h`;
}

const SOURCE_META: Record<
  ActivityEntry["source"],
  { label: string; dot: string; text: string }
> = {
  agent: { label: "AGENT", dot: "bg-accent", text: "text-accent" },
  user: { label: "YOU", dot: "bg-signal", text: "text-signal" },
  system: { label: "SYS", dot: "bg-faint", text: "text-faint" },
};

export function ActivityFeed({ entries }: { entries: ActivityEntry[] }) {
  const running = entries.some((e) => e.status === "running");

  return (
    <section className="panel flex min-h-0 flex-col">
      <header className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-2.5">
          <span className="relative flex h-2 w-2">
            {running && (
              <span className="absolute inline-flex h-full w-full animate-pulse-ring rounded-full bg-accent" />
            )}
            <span
              className={`relative inline-flex h-2 w-2 rounded-full ${
                running ? "bg-accent" : "bg-faint"
              }`}
            />
          </span>
          <h2 className="font-display text-sm font-medium tracking-wide">
            Agent Activity
          </h2>
        </div>
        {entries.length > 0 && (
          <button
            onClick={() => actions.clearActivity()}
            className="font-mono text-[10px] uppercase tracking-wider text-faint hover:text-muted"
          >
            clear
          </button>
        )}
      </header>

      <div className="scroll-slim flex min-h-0 flex-1 flex-col gap-0 overflow-y-auto p-2">
        {entries.length === 0 ? (
          <p className="px-3 py-6 text-center text-[11px] leading-relaxed text-faint">
            No activity yet. Tool calls from the in-browser agent and your own
            actions stream here in real time.
          </p>
        ) : (
          entries.map((e) => {
            const meta = SOURCE_META[e.source];
            return (
              <div
                key={e.id}
                className="animate-slide-in flex gap-2.5 rounded-lg px-2.5 py-2 hover:bg-surface-2/50"
              >
                <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${meta.dot}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span
                      className={`font-mono text-[10px] font-medium tracking-wider ${meta.text}`}
                    >
                      {meta.label}
                    </span>
                    {e.tool && (
                      <code className="truncate rounded bg-ink px-1.5 py-0.5 font-mono text-[10px] text-muted">
                        {e.tool}
                      </code>
                    )}
                    {e.status === "running" && (
                      <span className="font-mono text-[10px] text-accent">running…</span>
                    )}
                    {e.status === "error" && (
                      <span className="font-mono text-[10px] text-rose">error</span>
                    )}
                    <span className="ml-auto shrink-0 font-mono text-[10px] text-faint">
                      {timeAgo(e.at)}
                    </span>
                  </div>
                  <p
                    className={`mt-0.5 break-words text-xs leading-relaxed ${
                      e.status === "error" ? "text-rose/90" : "text-muted"
                    }`}
                  >
                    {e.message}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}
